// ABOUTME: ONE fact, in WALL clock — the instant the agent's queued voice runs out.
// ABOUTME: A deadline expires on its own, which is what lets it watchdog a flag that can latch.
/**
 * Exists because of the regression on top of the manual-tap speech gate (owner, 2026-09-15).
 *
 * `sendInteraction` now tells the backend whether the guest is hearing the agent, and the
 * backend drops a manual tap's spoken beat when the answer is yes
 * (`services/manual_tap_speech_gate.py`). That answer was built from two signals, and BOTH of
 * them can get STUCK on "speaking" — after which every tap the guest makes is silently
 * swallowed for the rest of the session, which is exactly the report: taps stop earning a
 * reply even when the agent has been quiet for a while.
 *
 *  - `isAgentSpeakingRef` is raised on the first PCM chunk in `handleAudioPlayback` and
 *    lowered only by the drain timer that the SAME function clears on entry and re-arms about
 *    thirty lines later. Anything that throws in between — a null `audioContext.current` at
 *    `ctx.state`, a zero-length chunk reaching `createBuffer`, a null analyser at `connect` —
 *    leaves the flag raised with no timer left to lower it. Permanent.
 *  - the playout cursor `nextStartTime - audioContext.currentTime` is measured on the AUDIO
 *    clock, which stops advancing whenever the browser suspends the context (mobile
 *    backgrounding, screen lock, an iOS audio interruption). Nothing in App.js resumes it
 *    except the next agent chunk, so with the cursor left in the future that difference stays
 *    positive for as long as nobody speaks again. Permanent, and mobile-first — which is where
 *    the report came from.
 *
 * Wall clock has neither failure mode: it always advances, and a deadline in it is over the
 * moment it passes. So the wire value becomes "the flag says speaking AND the queue it
 * scheduled has not yet run out", and a stuck flag is bounded by the audio it actually queued
 * instead of lasting the session.
 *
 * Pure and dependency-free, so both halves are unit-testable without an AudioContext.
 */

/**
 * Grace past the queue's end before the deadline calls it over, in milliseconds.
 *
 * Mirrors `handleAudioPlayback`'s own drain timer, which fires at `queue end + 200ms`. Keeping
 * the two equal is what makes this a WATCHDOG rather than a second opinion: in a healthy turn
 * the flag it guards is still the thing that decides, and this only bites once the flag has
 * outlived the audio that raised it.
 */
export const PLAYOUT_GRACE_MS = 200;

/**
 * The new end-of-queue instant after one more PCM chunk is scheduled.
 *
 * Mirrors the audio-clock scheduler it shadows: a chunk arriving after the queue has drained
 * starts a hair in the future (`lookaheadSeconds`, the same underrun cushion), one arriving
 * mid-utterance stacks onto the tail already scheduled. Never throws; a non-finite or
 * non-positive input is treated as "adds nothing".
 *
 * @param {number} deadlineMs       the current end-of-queue instant (0 when nothing is queued)
 * @param {number} nowMs            wall clock now, i.e. `Date.now()`
 * @param {number} chunkSeconds     duration of the chunk just scheduled
 * @param {number} [lookaheadSeconds] cushion applied ONLY when the queue had drained
 * @returns {number} the extended deadline, in the same wall-clock epoch as `nowMs`
 */
export function extendPlayoutDeadline(deadlineMs, nowMs, chunkSeconds, lookaheadSeconds = 0) {
  const prior = Number.isFinite(deadlineMs) ? deadlineMs : 0;
  const chunk = Number.isFinite(chunkSeconds) && chunkSeconds > 0 ? chunkSeconds : 0;
  const cushion = Number.isFinite(lookaheadSeconds) && lookaheadSeconds > 0 ? lookaheadSeconds : 0;
  const startMs = prior < nowMs ? nowMs + cushion * 1000 : prior;
  return startMs + chunk * 1000;
}

/**
 * Whether the queue that deadline describes could still be sounding.
 *
 * False for a deadline that was never set (0) as well as one that has passed, so a session in
 * which the agent has not spoken at all reads "not speaking" rather than "unknown".
 *
 * @param {number} deadlineMs  from {@link extendPlayoutDeadline}
 * @param {number} nowMs       wall clock now
 * @param {number} [graceMs]   defaults to {@link PLAYOUT_GRACE_MS}
 * @returns {boolean}
 */
export function isWithinPlayout(deadlineMs, nowMs, graceMs = PLAYOUT_GRACE_MS) {
  if (!Number.isFinite(deadlineMs) || deadlineMs <= 0) return false;
  if (!Number.isFinite(nowMs)) return false;
  const grace = Number.isFinite(graceMs) && graceMs > 0 ? graceMs : 0;
  return nowMs < deadlineMs + grace;
}
